import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleSelect } from '../styles/common/input.styled';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import { callStatusTone } from '../styles/consoleTheme';
import Pagination from '../components/common/Pagination';
import DebouncedSearchInput from '../components/common/DebouncedSearchInput';
import useApi from '../hooks/useApi';
import { getLocalDateInputValue } from '../utils/date';
import { formatPhoneNumber } from '../utils/format';
import AudioPlayerModal from '../components/routing/AudioPlayerModal';
import ConfirmModal from '../components/common/ConfirmModal';
import CallLogBatchDeleteModal from '../components/statistics/CallLogBatchDeleteModal';
import { downloadRecordFile } from '../api/audio';
import { buildRecordFileName, isRecordActionDisabled, saveBlobAsFile } from '../utils/recordDownload';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getCallStatusLabel } from '../utils/callStatus';
import * as T from '../styles/common/table.styled';
import { isTableRowActionTarget } from '../utils/tableInteraction';
import RealContactName from '../components/common/RealContactName';

// ─── Styled Components ───────────────────────────────────────────────────────

const PageContainer = T.PageContainer;



const FilterBar = styled.div`display: flex; flex-direction: column; gap: 12px; padding-bottom: 16px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};`;

const FilterHeader = styled.div`display: flex; align-items: center; gap: 8px;`;

const FilterHeading = styled.span`font-size: 12px; color: ${({ theme }) => theme.color.text_color_200};`;

const FilterSummary = styled.span<{ $active: boolean }>`
  padding: 3px 7px;
  border-radius: 5px;
  background-color: ${({ theme, $active }) => $active ? theme.color.point_y_100 + '18' : theme.color.primary_50};
  color: ${({ theme, $active }) => $active ? theme.color.point_y_100 : theme.color.text_color_400};
  font-size: 11px;
  font-weight: 700;
`;


const FilterGrid = styled.div`display: flex; flex-wrap: wrap; gap: 12px;`;

const FilterField = styled.div`display: flex; flex-direction: column; gap: 6px; min-width: 132px;`;

const FilterFieldLabel = styled.span<{ $active?: boolean }>`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300};`;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const DateSeparator = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.color.text_color_400};
  font-size: 12px;
`;

const FilterSelectWrap = styled.div<{ $active: boolean }>``;

const SearchInput = ConsoleInput;

const DateInput = styled(ConsoleInput)<{ $active?: boolean }>`width: 140px;`;

const FilterSelect = styled(ConsoleSelect)<{ $active?: boolean }>`min-width: 132px;`;

const FilterToolbar = styled.div`display: flex; align-items: flex-end; flex-wrap: wrap; gap: 8px;`;

const SearchField = styled.div`display: flex; flex-direction: column; gap: 6px; width: 360px; max-width: 100%;`;

const FilterActions = styled.div`display: flex; gap: 8px;`;

const SearchButton = styled(Button).attrs({ $variant: "secondary" })``;

const ResetButton = styled(Button).attrs({ $variant: "ghost" })``;

const TotalCount = T.TotalCount;

// 연결선 SVG 래퍼
const TableConnectorWrapper = styled.div<{ $hasTransfers: boolean }>`
  position: relative;
  min-width: ${({ $hasTransfers }) => $hasTransfers ? 1140 : 1108}px;
  ${({ $hasTransfers }) => $hasTransfers && `
    th:first-child, td:first-child:not([colspan]) { padding-left: 44px; }
  `}
`;

const ConnectorSvg = styled.svg`position: absolute; left: 0; top: 0; width: 32px; pointer-events: none; overflow: visible; z-index: 1;`;

const Table = T.Table;

const Th = T.Th;

const SortableHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.text_color_100};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.point_y_100};
    outline-offset: 3px;
    border-radius: 3px;
  }
`;

const SortIndicator = styled.span<{ $active: boolean }>`
  display: inline-flex;
  width: 14px;
  justify-content: center;
  color: ${({ theme, $active }) => $active ? theme.color.point_y_100 : theme.color.text_color_400};
  font-size: 14px;
  line-height: 1;
  opacity: 1;
`;

const Td = styled(T.Td)`height: 64px; white-space: normal; overflow-wrap: anywhere;`;

const PlayButton = T.ActionButton;

const DownloadButton = T.ActionButton;

const DetailButton = T.ActionButton;

const DeleteButton = styled(T.ActionButton).attrs({ $variant: "danger" })``;

const BatchDeleteButton = styled(Button).attrs({ $variant: "secondary" })`color: ${({ theme }) => theme.color.point_n_100};`;

const NoData = T.NoData;

// 전환 정보 배지
const TransferBadge = styled.button<{ $dir: 'in' | 'out' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  border: 1px solid ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
  background-color: ${({ $dir }) => $dir === 'in' ? '#2563EB15' : '#6F778715'};
  color: ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
  white-space: nowrap;

  &:hover {
    background-color: ${({ $dir }) => $dir === 'in' ? '#2563EB' : '#6F7787'};
    color: #fff;
  }
`;


// 당겨짐 배지
const PickupBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #059669;
  background-color: #05966915;
  color: #059669;
  white-space: nowrap;
`;

// 상태 뱃지
const BadgeIcon = styled.svg`
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  align-self: center;
  display: block;
`;

const StatusBadge = styled(T.Badge).attrs<{ $status: string }>(props => ({ $tone: callStatusTone(props.$status) }))<{ $status: string }>``;

const CallTypeBadge = styled(T.Badge).attrs<{ $type: string }>(props => ({ $tone: props.$type === "INBOUND" ? "info" : "neutral" }))<{ $type: string }>``;


// ─── 유틸 함수 (목록용) ──────────────────────────────────────────────────────

const getCallTypeLabel = (type: string) => {
  switch (type) {
    case 'INBOUND': return '수신';
    case 'OUTBOUND': return '발신';
    case 'INTERNAL': return '내선';
    case 'INNER': return '내선';
    default: return type;
  }
};

const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds && seconds !== 0) return '-';
  const totalSeconds = Math.round(seconds);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
};



const getDefaultStartDate = () => getLocalDateInputValue(-30);

// 전환 대상 표시 포맷: "이름 (번호)" 또는 "번호"
const formatTransferPerson = (username: string | null | undefined, phone: string | null | undefined): string => {
  if (!username && !phone) return '';
  if (username && phone) return `${username} (${phone})`;
  return username || phone || '';
};

const getDefaultEndDate = () => getLocalDateInputValue();

const CALL_STATUS_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'COMPLETED', label: '연결성공' },
  { value: 'MISSED', label: '부재중' },
] as const;

const CALL_TYPE_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'INBOUND', label: '수신' },
  { value: 'OUTBOUND', label: '발신' },
  { value: 'INNER', label: '내선' },
] as const;

const CALL_TIME_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'UNDER_10_SECONDS', label: '10초 미만' },
  { value: 'BETWEEN_10_SECONDS_AND_5_MINUTES', label: '10초~5분' },
  { value: 'BETWEEN_5_AND_30_MINUTES', label: '5~30분' },
  { value: 'BETWEEN_30_AND_60_MINUTES', label: '30~60분' },
  { value: 'AT_LEAST_60_MINUTES', label: '60분 이상' },
] as const;

const SORT_OPTIONS = [
  { value: 'latest', orderType: 'RINGINTAT', orderAsc: 'DESC' },
  { value: 'oldest', orderType: 'RINGINTAT', orderAsc: 'ASC' },
  { value: 'longest', orderType: 'CALLTIME', orderAsc: 'DESC' },
  { value: 'shortest', orderType: 'CALLTIME', orderAsc: 'ASC' },
] as const;

const DEFAULT_SORT = SORT_OPTIONS[0].value;

const getFilterValue = (
  value: string | null,
  options: readonly { value: string }[],
) => options.some((option) => option.value === value) ? value || options[0].value : options[0].value;

const getSortOption = (value: string) => SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];

type FilterDraftOverrides = Partial<Record<'keyword' | 'scope' | 'callStatus' | 'callType' | 'callTimeRange' | 'startDate' | 'endDate', string>>;

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

const CallHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, mode } = useAuth();
  const { data, loading, error, request } = useApi();

  const canDelete = useMemo(() => user?.permissionList?.includes('CALL_LOG_DELETE'), [user]);

  // URL 파라미터에서 현재 상태 추출
  const currentPage = parseInt(searchParams.get('pg') || '1');
  const appliedKeyword = searchParams.get('kw') || '';
  const appliedStartDate = searchParams.get('start') || getDefaultStartDate();
  const appliedEndDate = searchParams.get('end') || getDefaultEndDate();
  const appliedScope = searchParams.get('sc') || 'ALL';
  const appliedCallStatus = getFilterValue(searchParams.get('status'), CALL_STATUS_OPTIONS);
  const appliedCallType = getFilterValue(searchParams.get('type'), CALL_TYPE_OPTIONS);
  const appliedCallTimeRange = getFilterValue(searchParams.get('time'), CALL_TIME_OPTIONS);
  const appliedSort = getFilterValue(searchParams.get('sort'), SORT_OPTIONS);

  // 검색 입력 필드용 로컬 상태
  const [keyword, setKeyword] = useState(appliedKeyword);
  const [startDate, setStartDate] = useState(appliedStartDate);
  const [endDate, setEndDate] = useState(appliedEndDate);
  const [scope, setScope] = useState(appliedScope);
  const [callStatus, setCallStatus] = useState(appliedCallStatus);
  const [callType, setCallType] = useState(appliedCallType);
  const [callTimeRange, setCallTimeRange] = useState(appliedCallTimeRange);

  // 삭제 관련 상태
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'ID' | 'PHONE' | 'ALL';
    id?: number;
    phone?: string;
  } | null>(null);

  // 뒤로가기 등으로 URL이 변경될 때 검색 필드 동기화
  useEffect(() => {
    setKeyword(appliedKeyword);
    setStartDate(appliedStartDate);
    setEndDate(appliedEndDate);
    setScope(appliedScope);
    setCallStatus(appliedCallStatus);
    setCallType(appliedCallType);
    setCallTimeRange(appliedCallTimeRange);
  }, [appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange]);

  const [playbackInfo, setPlaybackInfo] = useState<{ url: string; name: string } | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // 연결선용 refs
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const connectorWrapperRef = useRef<HTMLDivElement>(null);
  const [connectors, setConnectors] = useState<Array<{ fromY: number; toY: number; fromId: number; toId: number }>>([]);
  const [hoveredConnectorIdx, setHoveredConnectorIdx] = useState<number | null>(null);

  const fetchList = useCallback(async (
    page: number,
    kw: string,
    start: string,
    end: string,
    sc: string,
    status: string,
    type: string,
    timeRange: string,
    sortValue: string,
  ) => {
    const sortOption = getSortOption(sortValue);
    await request({
      url: '/call/log',
      method: 'GET',
      params: {
        pg: page,
        limit: 10,
        keyword: kw || undefined,
        startDate: start || undefined,
        endDate: end || undefined,
        scope: sc,
        callStatus: status === 'ALL' ? undefined : status,
        callType: type === 'ALL' ? undefined : type,
        callTimeRange: timeRange === 'ALL' ? undefined : timeRange,
        orderType: sortOption.orderType,
        orderAsc: sortOption.orderAsc,
      }
    });
  }, [request]);

  useEffect(() => {
    if (mode) {
      fetchList(currentPage, appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange, appliedSort);
    }
  }, [currentPage, appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange, appliedSort, fetchList, mode]);

  const buildFilterParams = useCallback((overrides: FilterDraftOverrides = {}) => {
    const nextStartDate = overrides.startDate ?? startDate;
    const nextEndDate = overrides.endDate ?? endDate;
    const nextScope = overrides.scope ?? scope;
    const nextCallStatus = overrides.callStatus ?? callStatus;
    const nextCallType = overrides.callType ?? callType;
    const nextCallTimeRange = overrides.callTimeRange ?? callTimeRange;
    const nextKeyword = overrides.keyword ?? keyword;
    const params: any = { pg: '1' };
    if (nextKeyword) params.kw = nextKeyword;
    if (nextStartDate) params.start = nextStartDate;
    if (nextEndDate) params.end = nextEndDate;
    if (nextScope !== 'ALL') params.sc = nextScope;
    if (nextCallStatus !== 'ALL') params.status = nextCallStatus;
    if (nextCallType !== 'ALL') params.type = nextCallType;
    if (nextCallTimeRange !== 'ALL') params.time = nextCallTimeRange;
    if (appliedSort !== DEFAULT_SORT) params.sort = appliedSort;
    return params;
  }, [keyword, startDate, endDate, scope, callStatus, callType, callTimeRange, appliedSort]);

  const handleSearch = useCallback((nextKeyword?: string) => {
    setSearchParams(buildFilterParams(typeof nextKeyword === 'string' ? { keyword: nextKeyword } : undefined));
  }, [buildFilterParams, setSearchParams]);

  const handleSelectChange = useCallback((field: keyof FilterDraftOverrides, value: string) => {
    if (field === 'scope') setScope(value);
    if (field === 'callStatus') setCallStatus(value);
    if (field === 'callType') setCallType(value);
    if (field === 'callTimeRange') setCallTimeRange(value);
    setSearchParams(buildFilterParams({ [field]: value }));
  }, [buildFilterParams, setSearchParams]);

  const buildAppliedParams = useCallback((page: number, sortValue = appliedSort) => {
    const params: any = { pg: page.toString() };
    if (appliedKeyword) params.kw = appliedKeyword;
    if (appliedStartDate) params.start = appliedStartDate;
    if (appliedEndDate) params.end = appliedEndDate;
    if (appliedScope !== 'ALL') params.sc = appliedScope;
    if (appliedCallStatus !== 'ALL') params.status = appliedCallStatus;
    if (appliedCallType !== 'ALL') params.type = appliedCallType;
    if (appliedCallTimeRange !== 'ALL') params.time = appliedCallTimeRange;
    if (sortValue !== DEFAULT_SORT) params.sort = sortValue;
    return params;
  }, [appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange, appliedSort]);

  const handlePageChange = useCallback((page: number) => {
    setSearchParams(buildAppliedParams(page));
  }, [buildAppliedParams, setSearchParams]);

  const handleSort = useCallback((orderType: 'RINGINTAT' | 'CALLTIME') => {
    const currentSort = getSortOption(appliedSort);
    const nextDirection = currentSort.orderType === orderType && currentSort.orderAsc === 'DESC' ? 'ASC' : 'DESC';
    const nextSort = SORT_OPTIONS.find((option) => option.orderType === orderType && option.orderAsc === nextDirection) || SORT_OPTIONS[0];
    setSearchParams(buildAppliedParams(1, nextSort.value));
  }, [appliedSort, buildAppliedParams, setSearchParams]);

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartDate(value);
    setSearchParams(buildFilterParams({ startDate: value }));
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndDate(value);
    setSearchParams(buildFilterParams({ endDate: value }));
  };

  const activeFilterCount = [
    searchParams.has('start') || searchParams.has('end'),
    Boolean(appliedKeyword),
    appliedScope !== 'ALL',
    appliedCallStatus !== 'ALL',
    appliedCallType !== 'ALL',
    appliedCallTimeRange !== 'ALL',
    appliedSort !== DEFAULT_SORT,
  ].filter(Boolean).length;

  const hasActiveFilters = Boolean(
    searchParams.has('start') ||
    searchParams.has('end') ||
    appliedKeyword ||
    appliedScope !== 'ALL' ||
    appliedCallStatus !== 'ALL' ||
    appliedCallType !== 'ALL' ||
    appliedCallTimeRange !== 'ALL' ||
    appliedSort !== DEFAULT_SORT
  );

  const activeSort = getSortOption(appliedSort);
  const getSortButtonLabel = (label: string, orderType: 'RINGINTAT' | 'CALLTIME') => (
    activeSort.orderType === orderType
      ? `${label} 기준 정렬, 현재 ${activeSort.orderAsc === 'ASC' ? '오름차순' : '내림차순'}`
      : `${label} 기준 정렬`
  );

  const handleReset = useCallback(() => {
    setSearchParams({ pg: '1' });
  }, [setSearchParams]);

  // 삭제 처리 함수
  const executeDelete = async () => {
    if (!confirmDelete) return;

    try {
      await request({
        url: '/call/log',
        method: 'DELETE',
        data: {
          type: confirmDelete.type,
          callId: confirmDelete.id,
          phone: confirmDelete.phone,
        }
      });
      toast.success('삭제되었습니다.');
      setConfirmDelete(null);
      setIsBatchDeleteModalOpen(false);
      fetchList(currentPage, appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange, appliedSort);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handlePlay = async (call: any) => {
    if (isRecordActionDisabled(call)) return;

    if (playbackInfo) {
      URL.revokeObjectURL(playbackInfo.url);
      setPlaybackInfo(null);
    }
    try {
      setPlayingId(call.id);
      const blob = await downloadRecordFile(call.id);
      const url = URL.createObjectURL(blob);
      const name = buildRecordFileName(call, blob);
      setPlaybackInfo({ url, name });
    } catch (error) {
      toast.error('녹취 파일을 재생할 수 없습니다.');
      setPlayingId(null);
    }
  };

  const handleDownload = async (call: any) => {
    if (isRecordActionDisabled(call)) return;

    try {
      setDownloadingId(call.id);
      const blob = await downloadRecordFile(call.id);
      saveBlobAsFile(blob, buildRecordFileName(call, blob));
      toast.success('녹취 파일 다운로드를 시작했습니다.');
    } catch (error) {
      toast.error('녹취 파일을 다운로드할 수 없습니다.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleClosePlayer = () => {
    if (playbackInfo) URL.revokeObjectURL(playbackInfo.url);
    setPlaybackInfo(null);
    setPlayingId(null);
  };

  const calls = useMemo(() => (data as any)?.content || [], [data]);
  const totalPages = (data as any)?.totalPages ?? 0;
  const totalElements = (data as any)?.totalElements || 0;

  const transferPairs = useMemo(() => {
    const callIds = new Set(calls.map((call: any) => call.id));
    return calls
      .filter((call: any) => call.transfereeCallId && callIds.has(call.transfereeCallId))
      .map((call: any) => ({ fromId: call.id as number, toId: call.transfereeCallId as number }));
  }, [calls]);

  // 같은 페이지 내 돌려줌→돌려받음 연결선 계산
  const recalcConnectors = useCallback(() => {
    if (!tableBodyRef.current || calls.length === 0) {
      setConnectors([]);
      return;
    }
    if (transferPairs.length === 0) { setConnectors([]); return; }

    if (!connectorWrapperRef.current) { setConnectors([]); return; }
    const wrapperRect = connectorWrapperRef.current.getBoundingClientRect();
    const newConnectors = transferPairs.map(({ fromId, toId }: { fromId: number; toId: number }) => {
      const fromEl = rowRefs.current.get(fromId);
      const toEl = rowRefs.current.get(toId);
      if (!fromEl || !toEl) return null;
      // 순번 셀(첫번째 td) 중앙 Y 기준
      const fromTd = fromEl.cells[0] ?? fromEl;
      const toTd = toEl.cells[0] ?? toEl;
      const fromTdRect = fromTd.getBoundingClientRect();
      const toTdRect = toTd.getBoundingClientRect();
      return {
        fromY: fromTdRect.top - wrapperRect.top + fromTdRect.height / 2,
        toY: toTdRect.top - wrapperRect.top + toTdRect.height / 2,
        fromId,
        toId,
      };
    }).filter(Boolean) as Array<{ fromY: number; toY: number; fromId: number; toId: number }>;
    setConnectors(newConnectors);
  }, [calls, transferPairs]);

  useEffect(() => {
    recalcConnectors();
  }, [recalcConnectors]);

  // 리사이즈 시 SVG 좌표 재계산
  useEffect(() => {
    window.addEventListener('resize', recalcConnectors);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalcConnectors) : null;
    if (connectorWrapperRef.current) observer?.observe(connectorWrapperRef.current);
    return () => { window.removeEventListener('resize', recalcConnectors); observer?.disconnect(); };
  }, [recalcConnectors]);

  return (
    <PageContainer>
      <PageHeader title="통화 이력 조회" count={data && !loading && !error ? totalElements : undefined} actions={canDelete && <BatchDeleteButton onClick={() => setIsBatchDeleteModalOpen(true)}>일괄 삭제</BatchDeleteButton>} />

      <FilterBar>
        <FilterHeader>
          <FilterHeading>조회 조건</FilterHeading>
          <FilterSummary $active={activeFilterCount > 0}>
            {activeFilterCount > 0 ? `${activeFilterCount}개 적용` : '기본 조건'}
          </FilterSummary>
        </FilterHeader>
        <FilterGrid>
          <FilterField>
            <FilterFieldLabel $active={searchParams.has('start') || searchParams.has('end')}>기간</FilterFieldLabel>
            <DateRange>
              <DateInput aria-label="시작일" type="date" value={startDate} onChange={handleStartDateChange} $active={searchParams.has('start') || searchParams.has('end')} />
              <DateSeparator aria-hidden="true">~</DateSeparator>
              <DateInput aria-label="종료일" type="date" value={endDate} onChange={handleEndDateChange} $active={searchParams.has('start') || searchParams.has('end')} />
            </DateRange>
          </FilterField>
          <FilterField>
            <FilterFieldLabel $active={scope !== 'ALL'}>조회 범위</FilterFieldLabel>
            <FilterSelectWrap $active={scope !== 'ALL'}>
              <FilterSelect aria-label="조회 범위" value={scope} onChange={(e) => handleSelectChange('scope', e.target.value)} $active={scope !== 'ALL'}>
                <option value="ALL">전체</option>
                <option value="ME">내 이력</option>
              </FilterSelect>
            </FilterSelectWrap>
          </FilterField>
          <FilterField>
            <FilterFieldLabel $active={callStatus !== 'ALL'}>통화 상태</FilterFieldLabel>
            <FilterSelectWrap $active={callStatus !== 'ALL'}>
              <FilterSelect aria-label="통화 상태" value={callStatus} onChange={(e) => handleSelectChange('callStatus', e.target.value)} $active={callStatus !== 'ALL'}>
                {CALL_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </FilterSelect>
            </FilterSelectWrap>
          </FilterField>
          <FilterField>
            <FilterFieldLabel $active={callType !== 'ALL'}>통화 유형</FilterFieldLabel>
            <FilterSelectWrap $active={callType !== 'ALL'}>
              <FilterSelect aria-label="통화 유형" value={callType} onChange={(e) => handleSelectChange('callType', e.target.value)} $active={callType !== 'ALL'}>
                {CALL_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </FilterSelect>
            </FilterSelectWrap>
          </FilterField>
          <FilterField>
            <FilterFieldLabel $active={callTimeRange !== 'ALL'}>통화 시간</FilterFieldLabel>
            <FilterSelectWrap $active={callTimeRange !== 'ALL'}>
              <FilterSelect aria-label="통화 시간" value={callTimeRange} onChange={(e) => handleSelectChange('callTimeRange', e.target.value)} $active={callTimeRange !== 'ALL'}>
                {CALL_TIME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </FilterSelect>
            </FilterSelectWrap>
          </FilterField>
        </FilterGrid>
        <FilterToolbar>
          <SearchField>
            <FilterFieldLabel>키워드 검색</FilterFieldLabel>
            <SearchInput as={DebouncedSearchInput}
              aria-label="이름 또는 전화번호 검색"
              placeholder="전화번호·이름·입력/유입 번호 검색"
              value={keyword}
              onChange={handleKeywordChange}
              onSearch={handleSearch}
              delay={250}
            />
          </SearchField>
          <FilterActions>
            <SearchButton onClick={() => handleSearch()}>검색</SearchButton>
            {hasActiveFilters && <ResetButton onClick={handleReset}>초기화</ResetButton>}
          </FilterActions>

        </FilterToolbar>
      </FilterBar>



      <T.TableArea $stickyActions><TableConnectorWrapper ref={connectorWrapperRef} $hasTransfers={transferPairs.length > 0}>
        {connectors.length > 0 && (
          <ConnectorSvg role="img" aria-label="통화 전환 연결" style={{ height: connectorWrapperRef.current?.getBoundingClientRect().height || 0 }}>
            {connectors.map((c, i) => {
              const x1 = 24, cx = 4;
              const path = `M ${x1} ${c.fromY} C ${cx} ${c.fromY}, ${cx} ${c.toY}, ${x1} ${c.toY}`;
              const isHovered = hoveredConnectorIdx === i;
              return (
                <g
                  key={i}
                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredConnectorIdx(i)}
                  onMouseLeave={() => setHoveredConnectorIdx(null)}
                >
                  {/* 넓은 투명 패스 — hover 감지 영역 */}
                  <path d={path} fill="none" stroke="transparent" strokeWidth="12" />
                  <path d={path} fill="none" stroke="#6F7787" strokeWidth={isHovered ? 3 : 2} strokeDasharray="4 3" opacity={isHovered ? 1 : 0.8} />
                  <polygon
                    points={`${x1},${c.toY - 5} ${x1 + 6},${c.toY} ${x1},${c.toY + 5}`}
                    fill="#6F7787"
                    opacity={isHovered ? 1 : 0.8}
                  />
                </g>
              );
            })}
          </ConnectorSvg>
        )}
        <Table>
          <colgroup>{[transferPairs.length > 0 ? 88 : 56,88,96,168,168,128,88,128,188].map((width,index) => <col key={index} style={{ width }} />)}</colgroup>
          <thead>
            <tr>
              <Th>순번</Th>
              <Th>유형</Th>
              <Th>상태</Th>
              <Th>발신자</Th>
              <Th>수신자</Th>
              <Th>입력/유입 번호</Th>
              <Th aria-sort={activeSort.orderType === 'CALLTIME' ? activeSort.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}>
                <SortableHeaderButton type="button" aria-label={getSortButtonLabel('통화시간', 'CALLTIME')} onClick={() => handleSort('CALLTIME')}>
                  통화시간
                  <SortIndicator $active={activeSort.orderType === 'CALLTIME'} aria-hidden="true">
                    {activeSort.orderType === 'CALLTIME' ? activeSort.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}
                  </SortIndicator>
                </SortableHeaderButton>
              </Th>
              <Th aria-sort={activeSort.orderType === 'RINGINTAT' ? activeSort.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}>
                <SortableHeaderButton type="button" aria-label={getSortButtonLabel('시작일시', 'RINGINTAT')} onClick={() => handleSort('RINGINTAT')}>
                  시작일시
                  <SortIndicator $active={activeSort.orderType === 'RINGINTAT'} aria-hidden="true">
                    {activeSort.orderType === 'RINGINTAT' ? activeSort.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}
                  </SortIndicator>
                </SortableHeaderButton>
              </Th>
              <Th>관리</Th>
            </tr>
          </thead>
          <tbody ref={tableBodyRef}>
            {loading || (!data && !error) ? (
              <tr>
                <Td colSpan={9} style={{ textAlign: 'center' }}>로딩 중...</Td>
              </tr>
            ) : error ? <tr><Td colSpan={9}><PageState status="error" onRetry={() => fetchList(currentPage, appliedKeyword, appliedStartDate, appliedEndDate, appliedScope, appliedCallStatus, appliedCallType, appliedCallTimeRange, appliedSort)} /></Td></tr> : calls.length > 0 ? (
              calls.map((call: any, index: number) => {
                const isHighlighted = hoveredConnectorIdx !== null && (
                  connectors[hoveredConnectorIdx]?.fromId === call.id ||
                  connectors[hoveredConnectorIdx]?.toId === call.id
                );
                const recordActionDisabled = isRecordActionDisabled(call);
                return (
                <T.Tr
                  key={call.id}
                  $clickable
                  ref={(el) => { if (el) rowRefs.current.set(call.id, el); else rowRefs.current.delete(call.id); }}
                  onDoubleClick={(event) => {
                    if (isTableRowActionTarget(event.target)) return;
                    navigate(`/statistics/call-history/${call.id}`);
                  }}
                  style={isHighlighted ? { backgroundColor: '#6F778714', transition: 'background-color 0.15s' } : { transition: 'background-color 0.15s' }}
                >
                  <Td>{totalElements - (currentPage - 1) * 10 - index}</Td>
                  <Td>
                    <CallTypeBadge $type={call.callType}>
                      {call.callType === 'INBOUND' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 5 5 19M5 10v9h9" /></BadgeIcon>}
                      {call.callType === 'OUTBOUND' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 19 19 5M10 5h9v9" /></BadgeIcon>}
                      {getCallTypeLabel(call.callType)}
                    </CallTypeBadge>
                  </Td>
                  <Td>
                    <StatusBadge $status={call.callStatus}>
                      {call.callStatus === 'MISSED' && <BadgeIcon aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 2 6 6" /><path d="m22 2-6 6" /><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" /></BadgeIcon>}
                      {getCallStatusLabel(call.callStatus)}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <div>{mode === 'basic' && call.realSenderName ? <RealContactName name={call.realSenderName} /> : call.senderName || '미등록자'}</div>
                    <div style={{ fontSize: '13px', color: 'inherit', opacity: 0.85 }}>{formatPhoneNumber(call.senderPhone)}</div>
                    {call.transferPhone && call.transferPhone === call.senderPhone && (
                      <TransferBadge
                        $dir="out"
                        onClick={() => call.transfereeCallId && navigate(`/statistics/call-history/${call.transfereeCallId}`)}
                        title={`이 통화는 ${formatTransferPerson(call.transfereeUsername, call.transfereePhone)}에게 돌려준 전화입니다. 클릭하면 다음 통화를 확인합니다.`}
                      >
                        → 돌려줌&nbsp;{formatTransferPerson(call.transfereeUsername, call.transfereePhone)}
                      </TransferBadge>
                    )}
                  </Td>
                  <Td>
                    <div>{mode === 'basic' && call.realReceiverName ? <RealContactName name={call.realReceiverName} /> : call.receiverName || '미등록자'}</div>
                    <div style={{ fontSize: '13px', color: 'inherit', opacity: 0.85 }}>{formatPhoneNumber(call.receiverPhone)}</div>
                    {call.transfererPhone && (
                      <TransferBadge
                        $dir="in"
                        onClick={() => call.transfererCallId && navigate(`/statistics/call-history/${call.transfererCallId}`)}
                        title="이 통화는 돌려받은 전화입니다. 클릭하면 직전 통화를 확인합니다."
                      >
                        ← 돌려받음&nbsp;{formatTransferPerson(call.transfererUsername, call.transfererPhone)}
                      </TransferBadge>
                    )}
                    {call.transferPhone && call.transferPhone === call.receiverPhone && (
                      <TransferBadge
                        $dir="out"
                        onClick={() => call.transfereeCallId && navigate(`/statistics/call-history/${call.transfereeCallId}`)}
                        title={`이 통화는 ${formatTransferPerson(call.transfereeUsername, call.transfereePhone)}에게 돌려준 전화입니다. 클릭하면 다음 통화를 확인합니다.`}
                      >
                        → 돌려줌&nbsp;{formatTransferPerson(call.transfereeUsername, call.transfereePhone)}
                      </TransferBadge>
                    )}
                    {call.pickupTargetPhone && (
                      <PickupBadge title={`${call.pickupTargetUsername ? `${call.pickupTargetUsername} (${call.pickupTargetPhone})` : call.pickupTargetPhone}으로 부터 당겨짐`}>
                        ↙ 당겨짐&nbsp;{call.pickupTargetUsername ? `${call.pickupTargetUsername} (${call.pickupTargetPhone})` : call.pickupTargetPhone}
                      </PickupBadge>
                    )}
                  </Td>
                  <Td>{formatPhoneNumber(call.callNumber) || '-'}</Td>
                  <Td>{formatDuration(call.callTime)}</Td>
                  <Td>{call.ringingAt ? new Date(call.ringingAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'nowrap' }}>
                      <DetailButton onClick={() => navigate(`/statistics/call-history/${call.id}`)}>상세보기</DetailButton>
                      <PlayButton
                        onClick={() => handlePlay(call)}
                        disabled={recordActionDisabled || (playingId === call.id && !playbackInfo)}
                        title={recordActionDisabled ? '부재중 통화는 녹취 파일을 사용할 수 없습니다.' : undefined}
                       aria-label="녹취 재생">
                        ▶
                      </PlayButton>
                      <DownloadButton
                        aria-label="녹취 다운로드"
                        onClick={() => handleDownload(call)}
                        disabled={recordActionDisabled || downloadingId === call.id}
                        title={recordActionDisabled ? '부재중 통화는 녹취 파일을 사용할 수 없습니다.' : undefined}
                      >
                        {downloadingId === call.id ? '…' : '↓'}
                      </DownloadButton>
                      {canDelete && (
                        <DeleteButton onClick={() => setConfirmDelete({ type: 'ID', id: call.id })}>
                          삭제
                        </DeleteButton>
                      )}
                    </div>
                  </Td>
                </T.Tr>
                );
              })
            ) : (
              <tr>
                <Td colSpan={9}>
                  <NoData>조회된 통화 이력이 없습니다.</NoData>
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableConnectorWrapper></T.TableArea>

      <T.TableFooter>{data && !loading && !error && <TotalCount>
        총 <span>{totalElements}</span>건
      </TotalCount>}<Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      /></T.TableFooter>

      {playbackInfo && (
        <AudioPlayerModal
          url={playbackInfo.url}
          name={playbackInfo.name}
          onClose={handleClosePlayer}
        />
      )}

      {isBatchDeleteModalOpen && (
        <CallLogBatchDeleteModal
          onClose={() => setIsBatchDeleteModalOpen(false)}
          onDelete={(type, phone) => setConfirmDelete({ type, phone })}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="통화 이력 삭제"
          message={`${
            confirmDelete.type === 'ID'
              ? '해당 통화를 삭제하시겠습니까?'
              : confirmDelete.type === 'PHONE'
              ? '해당 전화번호에 대한 통화를 삭제하시겠습니까?'
              : '전체 통화기록을 삭제하시겠습니까?'
          }\n\n주의사항으로 해당 통화와 연관된 데이터들이 같이 삭제됩니다.`}
          confirmLabel="삭제"
          onConfirm={executeDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </PageContainer>
  );
};

export default CallHistoryPage;
